/**
 * Upload modal with drag-and-drop support.
 */
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  XMarkIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui';
import { useUploadImage } from '@/hooks/queries/useMediaQueries';
import type { ImageCategory, MediaFolder, MediaTag, UploadProgress } from '@/types/media';
import { formatFileSize } from '@/types/media';

type UploadCategory = Exclude<ImageCategory, 'targeted'>;

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  folders: MediaFolder[];
  tags: MediaTag[];
  currentFolderId: string | null;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function UploadModal({
  isOpen,
  onClose,
  folders,
  tags,
  currentFolderId,
}: UploadModalProps) {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>(currentFolderId || '');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<UploadCategory>('approved');
  const uploadMutation = useUploadImage();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newUploads: UploadProgress[] = acceptedFiles.map((file) => ({
      file,
      progress: 0,
      status: 'pending' as const,
    }));
    setUploads((prev) => [...prev, ...newUploads]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
    maxSize: MAX_FILE_SIZE,
    onDropRejected: (rejections) => {
      rejections.forEach((rejection) => {
        const errors = rejection.errors.map((e) => e.message).join(', ');
        setUploads((prev) => [
          ...prev,
          {
            file: rejection.file,
            progress: 0,
            status: 'error',
            error: errors,
          },
        ]);
      });
    },
  });

  const startUpload = async () => {
    for (let i = 0; i < uploads.length; i++) {
      const upload = uploads[i];
      if (upload.status !== 'pending') continue;

      // Update status to uploading
      setUploads((prev) =>
        prev.map((u, idx) => (idx === i ? { ...u, status: 'uploading' as const, progress: 50 } : u))
      );

      try {
        await uploadMutation.mutateAsync({
          file: upload.file,
          options: {
            folderId: selectedFolderId || undefined,
            tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
            category: selectedCategory,
          },
        });

        // Update status to success
        setUploads((prev) =>
          prev.map((u, idx) =>
            idx === i ? { ...u, status: 'success' as const, progress: 100 } : u
          )
        );
      } catch (err) {
        // Update status to error
        console.error('Upload error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        setUploads((prev) =>
          prev.map((u, idx) =>
            idx === i
              ? { ...u, status: 'error' as const, error: errorMessage }
              : u
          )
        );
      }
    }
  };

  const removeUpload = (index: number) => {
    setUploads((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    setUploads([]);
    setSelectedFolderId(currentFolderId || '');
    setSelectedTagIds([]);
    setSelectedCategory('approved');
    onClose();
  };

  const pendingCount = uploads.filter((u) => u.status === 'pending').length;
  const completedCount = uploads.filter((u) => u.status === 'success').length;
  const isUploading = uploads.some((u) => u.status === 'uploading');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Upload Images</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 space-y-4">
            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                transition-colors
                ${isDragActive
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400'
                }
              `}
            >
              <input {...getInputProps()} />
              <CloudArrowUpIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-sm text-gray-600">
                {isDragActive
                  ? 'Drop files here...'
                  : 'Drag & drop images here, or click to select'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                JPEG, PNG, GIF, WebP up to 5MB
              </p>
            </div>

            {/* Category */}
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </legend>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="upload-category"
                    value="approved"
                    checked={selectedCategory === 'approved'}
                    onChange={() => setSelectedCategory('approved')}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span>Approved <span className="text-gray-400">(default)</span></span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="upload-category"
                    value="reference"
                    checked={selectedCategory === 'reference'}
                    onChange={() => setSelectedCategory('reference')}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span>Reference <span className="text-gray-400">(studio input only)</span></span>
                </label>
              </div>
            </fieldset>

            {/* Options */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Folder
                </label>
                <select
                  value={selectedFolderId}
                  onChange={(e) => setSelectedFolderId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Root (No folder)</option>
                  {folders.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.path}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <div className="flex flex-wrap gap-1.5 min-h-[38px] p-1.5 border border-gray-300 rounded-md">
                  {tags.length === 0 ? (
                    <span className="text-sm text-gray-400 px-1.5 py-0.5">No tags available</span>
                  ) : (
                    tags.map((tag) => {
                      const isSelected = selectedTagIds.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedTagIds(selectedTagIds.filter((id) => id !== tag.id));
                            } else {
                              setSelectedTagIds([...selectedTagIds, tag.id]);
                            }
                          }}
                          className={`
                            inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                            transition-all cursor-pointer
                            ${isSelected
                              ? 'ring-2 ring-offset-1'
                              : 'opacity-60 hover:opacity-100'
                            }
                          `}
                          style={{
                            backgroundColor: `${tag.color}20`,
                            color: tag.color,
                            ...(isSelected ? { ringColor: tag.color } : {}),
                          }}
                        >
                          {tag.name}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Upload list */}
            {uploads.length > 0 && (
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {uploads.map((upload, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 text-sm"
                  >
                    {upload.status === 'success' && (
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    )}
                    {upload.status === 'error' && (
                      <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                    )}
                    {(upload.status === 'pending' || upload.status === 'uploading') && (
                      <div className="w-5 h-5 flex-shrink-0">
                        {upload.status === 'uploading' ? (
                          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                        )}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{upload.file.name}</p>
                      {upload.error && (
                        <p className="text-xs text-red-500">{upload.error}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">
                      {formatFileSize(upload.file.size)}
                    </span>
                    {upload.status !== 'uploading' && (
                      <button
                        onClick={() => removeUpload(index)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t bg-gray-50">
            <div className="text-sm text-gray-500">
              {pendingCount > 0 && `${pendingCount} pending`}
              {pendingCount > 0 && completedCount > 0 && ' · '}
              {completedCount > 0 && `${completedCount} uploaded`}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={handleClose}>
                {completedCount > 0 && pendingCount === 0 ? 'Done' : 'Cancel'}
              </Button>
              {pendingCount > 0 && (
                <Button onClick={startUpload} disabled={isUploading}>
                  {isUploading ? 'Uploading...' : `Upload ${pendingCount} file${pendingCount > 1 ? 's' : ''}`}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
